import React, { useMemo, useState } from 'react';
import { ClassName, ComponentDeep } from '../../redux/methodologyDeep/models';
import { Column } from '../simpleColumn/simpleColumn';
import { ColorOptions } from '../typo/STTypo.models';
import CanonicalKeyGlyph from '../../assets/icons/Glyph/CanonicalKeyGlyph';
import { prepareComponents, uuid } from '../../helpers';
import { useCharacteristicClustering } from '../../containers/MethodologyContainer/hooks/use-characteristic-clustering';
import { useSelector } from 'react-redux';
import { AppState } from '../../redux/redux.model';
import { getCurrentCharacteristicsComponentsInPhenomenonFromMethodologyDeep } from './clusterModal.utils';
import { MethodologyCluster } from '../../redux/methodologyCluster/models';
import { handleClusterName } from '../clusterTag/useCluster';
import {
  getCharacteristicsIdsByPhenomenonIdExcludingCluster,
  getUsedClusterNamesByPhenomenonId
} from '../../redux/methodologyCluster/selectors';
import { PhenomenonIdTuple } from './clusterModal.models';

interface UseModalClusterProps {
  cluster?: MethodologyCluster;
  phenomenonUniqueId: string;
  phenomenonId: string;
  components: ComponentDeep[];
  onClose: () => void;
}

export function useModalCluster({ cluster, phenomenonUniqueId, phenomenonId, components, onClose }: UseModalClusterProps) {
  const usedClusterNames = useSelector(getUsedClusterNamesByPhenomenonId(phenomenonId));
  const [clusterName, setClusterName] = useState(() => {
    return cluster ? handleClusterName(cluster.name) : '';
  });
  const { handleCreateCluster, handleEditCluster } = useCharacteristicClustering();
  const modalTitle = useMemo(() => {
    return cluster?.name.localized_strings ? 'EDIT CLUSTER' : 'CREATE CLUSTER';
  }, [cluster]);

  const columns = [
    Column({
      color: ColorOptions.PRIMARY,
      key: 'comesFromCanonical',
      keyName: 'comesFromCanonical',
      width: '6%',
      IdIntl: 'table.title.type',
      render: object => <CanonicalKeyGlyph color={!object.comesFromCanonical && '#C1C5C8'} />
    }),
    Column({
      color: ColorOptions.PRIMARY,
      key: 'name',
      keyName: 'name',
      width: '94%',
      IdIntl: 'table.title.name'
    })
  ];

  const isEditingCluster = !!cluster?.name;

  const isNameInUse = useMemo(() => {
    if (!usedClusterNames || usedClusterNames.length === 0) return false;
    const filteredNames =
      isEditingCluster && cluster?.name
        ? usedClusterNames.filter(name => name.toLowerCase() !== handleClusterName(cluster.name).toLowerCase())
        : usedClusterNames;
    return filteredNames.some(name => name.toLowerCase() === clusterName.trim().toLowerCase());
  }, [usedClusterNames, clusterName, isEditingCluster, cluster]);

  const parsedComponents = useMemo(() => {
    return prepareComponents(components);
  }, [components]);

  const handleSubmit = (selectedKeys: string[]) => {
    if (clusterName.trim() === '' || selectedKeys.length === 0) return;

    const newCluster: MethodologyCluster = {
      id: cluster?.id ?? uuid(),
      name: { localized_strings: { en: clusterName } },
      components_ids: selectedKeys,
      components_class_name: ClassName.CHARACTERISTIC,
      phenomenon_id: phenomenonId,
      phenomenon_unique_id: phenomenonUniqueId
    };

    if (!cluster) {
      handleCreateCluster(newCluster);
    } else {
      handleEditCluster(newCluster);
    }
    setClusterName('');
    onClose();
  };

  return {
    clusterName,
    setClusterName,
    modalTitle,
    columns,
    parsedComponents,
    handleSubmit,
    isNameInUse
  };
}

export function useClusterModalComponents({ phenomenonIds, clusterId }: { phenomenonIds?: PhenomenonIdTuple; clusterId?: string }) {
  const [id, phenomenonId] = phenomenonIds || [];
  const methodologyDeep = useSelector((state: AppState) => state.MethodologyDeep.methodologyDeep);
  const usedCharacteristicsIds = useSelector(getCharacteristicsIdsByPhenomenonIdExcludingCluster(phenomenonId ?? '', clusterId));

  const components = useMemo(() => {
    if (!methodologyDeep || !phenomenonId || !id) return [];
    const usedIds = new Set(usedCharacteristicsIds);
    const allCharacteristicsInPhenomenon = getCurrentCharacteristicsComponentsInPhenomenonFromMethodologyDeep(
      methodologyDeep,
      phenomenonId,
      id
    );

    return allCharacteristicsInPhenomenon.filter(
      component => component.characteristic?.characteristicId && !usedIds.has(component.characteristic?.characteristicId)
    );
  }, [methodologyDeep, phenomenonId, id, usedCharacteristicsIds]);

  return { components };
}
